import React, { useContext, useState } from "react";
import classNames from "classnames";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { LeftNavClosedIcon, LeftNavOpenedIcon } from "@icons";
import { SandboxPanelContext } from "./sandboxPanelContext";
import styles from "./ShowcasePreview.module.scss";

export type StageBackground = "grid" | "white" | "gray";
export type StageAlignment = "center" | "start" | "stretch";
export type StageWidth = "default" | "wide" | "full";

const STAGE_BACKGROUND_STORAGE_KEY = "kick.designSystem.stageBackground";

const DEFAULT_STAGE_BACKGROUND: StageBackground = "grid";

const isStageBackground = (value: string | null): value is StageBackground =>
    value === "grid" || value === "white" || value === "gray";

const readStoredStageBackground = (): StageBackground => {
    try {
        const storedValue = localStorage.getItem(STAGE_BACKGROUND_STORAGE_KEY);
        if (isStageBackground(storedValue)) {
            return storedValue;
        }
    } catch {
        // Unavailable or blocked storage — fall back to the default background.
    }
    return DEFAULT_STAGE_BACKGROUND;
};

const writeStoredStageBackground = (background: StageBackground) => {
    try {
        localStorage.setItem(STAGE_BACKGROUND_STORAGE_KEY, background);
    } catch {
        // Storage unavailable — the mounted page already switched, so skip.
    }
};

const stageBackgroundOptions: ReadonlyArray<{
    id: StageBackground;
    label: string;
}> = [
    { id: "grid", label: "Grid background" },
    { id: "white", label: "White background" },
    { id: "gray", label: "Gray background" },
];

const stageBackgroundClassNames: Record<StageBackground, string> = {
    grid: styles.stageGrid,
    white: styles.stageWhite,
    gray: styles.stageGray,
};

const swatchClassNames: Record<StageBackground, string> = {
    grid: styles.swatchGrid,
    white: styles.swatchWhite,
    gray: styles.swatchGray,
};

const stageAlignmentClassNames: Record<StageAlignment, string | undefined> = {
    center: undefined,
    start: styles.stageStart,
    stretch: styles.stageStretch,
};

const showcaseWidthClassNames: Record<StageWidth, string | undefined> = {
    default: undefined,
    wide: styles.showcaseWide,
    full: styles.showcaseFull,
};

/**
 * @purpose Shared preview frame for every design system showcase — owns the
 * preview header, the switchable stage background, alignment and width of the
 * showcased component, and the props readout below it.
 *
 * `belowStage` renders between the stage and the props readout, off the stage
 * background and at the preview's full width, for secondary sections a story
 * showcases alongside its main demo (e.g. a `VariantGrid`).
 */
export const ShowcasePreview = ({
    summary,
    alignment = "center",
    width = "default",
    belowStage,
    output,
    children,
}: {
    summary?: React.ReactNode;
    alignment?: StageAlignment;
    width?: StageWidth;
    belowStage?: React.ReactNode;
    output?: string;
    children: React.ReactNode;
}) => {
    const [background, setBackground] = useState<StageBackground>(
        readStoredStageBackground,
    );
    const panel = useContext(SandboxPanelContext);

    return (
        <section className={styles.preview}>
            <div className={styles.previewHeading}>
                <span>Preview</span>
                <HStack gap={8} align="center">
                    {summary ? (
                        <span className={styles.summary}>{summary}</span>
                    ) : null}
                    {stageBackgroundOptions.map((option) => (
                        <Tooltip key={option.id} content={option.label}>
                            <Button
                                iconOnly
                                variant="secondary"
                                size="sm"
                                aria-label={option.label}
                                aria-pressed={background === option.id}
                                className={classNames({
                                    [styles.backgroundButtonActive]:
                                        background === option.id,
                                })}
                                onClick={() => {
                                    setBackground(option.id);
                                    writeStoredStageBackground(option.id);
                                }}
                            >
                                <span
                                    className={classNames(
                                        styles.swatch,
                                        swatchClassNames[option.id],
                                    )}
                                />
                            </Button>
                        </Tooltip>
                    ))}
                    {panel.hasControls ? (
                        <Tooltip
                            key={panel.isPanelOpen ? "open" : "closed"}
                            content={
                                panel.isPanelOpen
                                    ? "Hide controls"
                                    : "Show controls"
                            }
                        >
                            <Button
                                iconOnly
                                variant="secondary"
                                size="sm"
                                aria-label={
                                    panel.isPanelOpen
                                        ? "Hide controls"
                                        : "Show controls"
                                }
                                onClick={panel.togglePanel}
                            >
                                <span className={styles.panelToggleIcon}>
                                    {panel.isPanelOpen ? (
                                        <LeftNavOpenedIcon />
                                    ) : (
                                        <LeftNavClosedIcon />
                                    )}
                                </span>
                            </Button>
                        </Tooltip>
                    ) : null}
                </HStack>
            </div>
            <div
                className={classNames(
                    styles.stage,
                    stageBackgroundClassNames[background],
                    stageAlignmentClassNames[alignment],
                )}
            >
                <div
                    className={classNames(
                        styles.showcase,
                        showcaseWidthClassNames[width],
                    )}
                >
                    {children}
                </div>
            </div>
            {belowStage}
            {output ? (
                <div className={styles.output}>
                    <span>Props</span>
                    <pre>{output}</pre>
                </div>
            ) : null}
        </section>
    );
};
